import type { ReactNode } from "react";
import Animated, { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { StyleSheet, View } from "@-/com.npmjs.react-native";
import type { LearnCardFace } from "@-/dev.improve.haniverse.lib.learn.deck";
import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

const styles = StyleSheet.create({
  fill: StyleSheet.absoluteFill,
});

/**
 * The STUDY surface of a card: its front and back, both mounted, the back underneath the front.
 * An upward swipe slides the whole front off the top while fading it out; the back fades in
 * and rises slightly into place. Both transforms include their shells, not just their content,
 * and the back's shell includes its binder tabs (`backBinderTabs`): they are part of the back
 * face, so wherever and whenever the back is drawn, they are, and never otherwise.
 * All other reveals set progress at once.
 * The face turned away receives no touches or accessibility focus.
 */
export function LearnFlashcardStudySurface({
  back,
  backBinderTabs,
  face,
  front,
  isInteractive,
  isLesson,
  isSlideshow = false,
  presentationKey,
  showRevealHint,
  stage,
}: {
  back: ReactNode;
  backBinderTabs: ReactNode;
  face: LearnCardFace;
  front: ReactNode;
  isInteractive: boolean;
  isLesson: boolean;
  isSlideshow?: boolean;
  presentationKey: string;
  showRevealHint: boolean;
  stage: LearnDeckStageValues;
}) {
  const frontStyle = useAnimatedStyle(() => {
    // A successor must never inherit the current card's reveal progress. Lessons swap faces
    // immediately, with visibility tied to the same React state that activates the back.
    if (isLesson || stage.motionPresentation.get().current !== presentationKey) {
      return { opacity: face === "front" ? 1 : 0, transform: [] };
    }
    const progress = stage.flipProgress.get();
    return {
      opacity: 1 - progress,
      transform: [{ translateY: -stage.cardHeight.get() * progress }],
    };
  });
  const backStyle = useAnimatedStyle(() => {
    if (isLesson || stage.motionPresentation.get().current !== presentationKey) {
      return { opacity: face === "back" ? 1 : 0, transform: [] };
    }
    const progress = stage.flipProgress.get();
    return {
      opacity: progress,
      transform: [{ translateY: 24 * (1 - progress) }],
    };
  });

  return (
    <>
      {back === null ? null : (
        <LearnFlashcardShell
          animatedStyle={backStyle}
          binderTabs={
            <View
              accessibilityElementsHidden={face !== "back"}
              className="flex-1"
              importantForAccessibility={face === "back" ? "auto" : "no-hide-descendants"}
              pointerEvents="box-none"
            >
              {backBinderTabs}
            </View>
          }
          className="absolute inset-0"
          pointerEvents={isInteractive && face === "back" ? "auto" : "none"}
          testID="learn-card-back"
        >
          <Animated.View
            accessibilityElementsHidden={face !== "back"}
            className="flex-1"
            importantForAccessibility={face === "back" ? "auto" : "no-hide-descendants"}
          >
            {back}
          </Animated.View>
        </LearnFlashcardShell>
      )}
      <LearnFlashcardShell
        animatedStyle={frontStyle}
        // A slideshow draws its own slides, and its neighbors run past the card to the screen edge.
        contentClassName={isSlideshow ? "overflow-visible border-0 bg-transparent" : undefined}
        pointerEvents={isInteractive && face === "front" ? "auto" : "none"}
        style={styles.fill}
        testID="learn-card-front"
      >
        <Animated.View
          accessibilityElementsHidden={face !== "front"}
          className="flex-1"
          importantForAccessibility={face === "front" ? "auto" : "no-hide-descendants"}
        >
          {front}
          {showRevealHint ? (
            <Text
              className="absolute inset-x-14 bottom-2.25 text-center text-sm"
              colorVariant="muted"
              pointerEvents="none"
              testID="learn-card-reveal-hint"
            >
              Tap to reveal
            </Text>
          ) : null}
        </Animated.View>
      </LearnFlashcardShell>
    </>
  );
}

/**
 * The INFO surface draws its own binder tabs, Details connected to its body, in the same shell, so
 * the two read (and move) as one surface.
 */
export function LearnFlashcardInfoSurface({
  binderTabs,
  children,
  isInteractive,
}: {
  binderTabs: ReactNode;
  children: ReactNode;
  isInteractive: boolean;
}) {
  return (
    <LearnFlashcardShell
      binderTabs={binderTabs}
      className="absolute inset-0"
      pointerEvents={isInteractive ? "auto" : "none"}
      testID="learn-card-info"
    >
      <View className="flex-1">{children}</View>
    </LearnFlashcardShell>
  );
}
